/** The two answers the ledger gives about a chain, kept apart (ADR-0020) — shared by the list and by
 * an entry's own page. Moved out of `Ledger.tsx` unchanged: the sentence a reader sees for "does it
 * add up" and "is it committed" must be one sentence wherever they meet it. */

import type { ChainCheck } from "../api/ledger";
import { StatusPill } from "../components/StatusPill";
import { ANCHOR, CHECK } from "./ledgerWords";
import own from "./Ledger.module.css";

function cls(...names: Array<string | false | undefined>): string {
  return names.filter(Boolean).join(" ");
}

/** The two answers, kept apart (ADR-0020). */
export function Integrity({ check }: { check: ChainCheck }) {
  const state = CHECK[check.state] ?? {
    word: `Unrecognised result: ${check.state}`,
    short: "Unrecognised",
    tone: "neutral" as const,
  };
  const anchor = ANCHOR[check.anchor_state];
  return (
    <div className={cls(own.check)} data-testid="ledger-check">
      <div className={cls(own.answers)}>
        <div className={cls(own.answer)}>
          <span className={cls(own.question)}>Does the chain add up?</span>
          <p className={cls(own.verdict)} data-testid="ledger-state">
            <StatusPill tone={state.tone}>{state.short}</StatusPill>
            <strong>{state.word}</strong>
            {check.checked_from_seq === null || check.checked_to_seq === null ? null : (
              <span className={cls(own.verdictRange)}>
                {check.entries_checked} recomputed, #{check.checked_from_seq} to #
                {check.checked_to_seq}
              </span>
            )}
            {check.reason ? <span className={cls(own.verdictRange)}>{check.reason}</span> : null}
          </p>
        </div>

        {/* **The second question, separately.** A chain that verifies inside a database somebody
            controls shows internal consistency; whether a head has been committed somewhere it
            cannot be rewritten is what says the history has not moved. One tick for both would
            make an unanchored chain look attested. */}
        <div className={cls(own.answer)}>
          <span className={cls(own.question)}>Is it committed where it cannot be rewritten?</span>
          <p className={cls(own.anchor)} data-testid="ledger-anchor">
            <StatusPill tone={anchor?.tone ?? "neutral"}>{anchor?.short ?? "Unrecognised"}</StatusPill>
            <strong>{anchor?.head ?? `Anchor state: ${check.anchor_state}`}</strong>
            <span className={cls(own.anchorState)}>
              {anchor?.detail(check) ??
                "This product does not recognise that state, so nothing here should be read as a claim."}
            </span>
          </p>
        </div>
      </div>

      <ul className={cls(own.caveats)}>
        {check.caveats.map((caveat) => (
          <li key={caveat} className={cls(own.caveat)} data-testid="ledger-caveat">
            {caveat}
          </li>
        ))}
      </ul>
    </div>
  );
}

